
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import SolicitudDetallePanel from '@/components/adelantos/SolicitudDetallePanel';
import CancelAdvanceCard from '@/components/adelantos/CancelAdvanceCard';
import { SolicitudDetalle } from '@/components/adelantos/types';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { Link } from 'react-router-dom';

function SkeletonDetail() {
  return (
    <div className="py-8 animate-pulse">
      <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded mb-6" />
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 mb-8">
        <div className="flex justify-between items-center w-full">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700" />
              <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="h-5 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
        <div className="p-6 grid grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-slate-100 dark:bg-slate-800 rounded-lg" />
          ))}
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex gap-4">
              <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SolicitudDetallePage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';

  const [solicitud, setSolicitud] = useState<SolicitudDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const detailUrl = getEndpointUrl('adelantos', 'detalle').replace('{id}', id);
        const detail = await httpClient.get<SolicitudDetalle>(detailUrl);
        setSolicitud(detail);
      } catch (err: any) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <AppLayout wide>
        <SkeletonDetail />
      </AppLayout>
    );
  }

  if (notFound || !solicitud) {
    return (
      <AppLayout wide>
        <div className="py-16 text-center">
          <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-4 block">
            search_off
          </span>
          <p className="text-lg font-semibold text-slate-600 dark:text-slate-400">Solicitud no encontrada</p>
          <Link
            to="/adelantos/solicitudes"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            Volver a Solicitudes
          </Link>
        </div>
      </AppLayout>
    );
  }

  const handleCancelled = (updatedEstado: SolicitudDetalle['estado'], rejectionReason: string | null) => {
    setSolicitud((prev) => (prev ? { ...prev, estado: updatedEstado, motivoRechazo: rejectionReason ?? prev.motivoRechazo } : prev));
  };

  return (
    <AppLayout wide>
      <SolicitudDetallePanel solicitud={solicitud} />
      <CancelAdvanceCard solicitud={solicitud} onCancelled={handleCancelled} />
      <p className="mt-4 text-xs text-muted-foreground">
        La programación de cobranza de cada cuota vive ahora en{' '}
        <Link to="/cartola" className="text-flux-primary hover:underline">/cartola</Link>.
      </p>
    </AppLayout>
  );
}
